import { useCallback, useState } from 'react';

import { confirmCoupon } from '@shared/api/giftCoupon/giftCoupon.controller';
import { GiftCouponForStore } from '@shared/api/giftCoupon/giftCoupon.interface';
import { confirmDelivery } from '@shared/api/giftDelivery/giftDelivery.controller';
import { GiftDeliveryForStore } from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftStoreRole } from '@shared/api/giftOrder/giftOrder.interface';

import { PendingConfirmItem } from './giftShareTypes';

interface UseGiftMemoModalParams {
  role: GiftStoreRole;
  /** 저장 성공 후 주문 데이터 무효화(refetch) 콜백 */
  invalidateOrder: () => void;
}

/**
 * "메모 남기기" 모달. 쿠폰/배송 모두 confirm API(메모 동봉)로 저장한다.
 * useGiftOrderShare 에서 분리 — role, invalidateOrder 에만 의존하는 독립 모달.
 */
export function useGiftMemoModal({ role, invalidateOrder }: UseGiftMemoModalParams) {
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [pendingMemoItem, setPendingMemoItem] = useState<PendingConfirmItem | null>(null);
  const [isMemoSaving, setIsMemoSaving] = useState(false);

  const handleOpenMemoCoupon = useCallback((coupon: GiftCouponForStore) => {
    setPendingMemoItem({ type: 'coupon', item: coupon });
    setShowMemoModal(true);
  }, []);

  const handleOpenMemoDelivery = useCallback((delivery: GiftDeliveryForStore) => {
    setPendingMemoItem({ type: 'delivery', item: delivery });
    setShowMemoModal(true);
  }, []);

  const handleMemoSave = useCallback(
    async (memo: string) => {
      if (!pendingMemoItem) return;
      const trimmed = memo.trim();
      if (!trimmed) return;
      setIsMemoSaving(true);
      try {
        if (pendingMemoItem.type === 'coupon') {
          await confirmCoupon(pendingMemoItem.item._id, role, trimmed);
        } else {
          await confirmDelivery(pendingMemoItem.item._id, role, trimmed);
        }
        invalidateOrder();
      } catch (error) {
        console.error('메모 저장 실패:', error);
      } finally {
        setIsMemoSaving(false);
        setShowMemoModal(false);
        setPendingMemoItem(null);
      }
    },
    [pendingMemoItem, role, invalidateOrder],
  );

  const handleMemoClose = useCallback(() => {
    setShowMemoModal(false);
    setPendingMemoItem(null);
  }, []);

  return {
    showMemoModal,
    isMemoSaving,
    memoExistingValue: pendingMemoItem?.item.memo,
    handleOpenMemoCoupon,
    handleOpenMemoDelivery,
    handleMemoSave,
    handleMemoClose,
  };
}
